import type { Ref } from "vue";

import { isObject, mapValues } from "radash";

import type { LocaleMorpheus } from "@solvari/translations";

import type {
  ValidationRule,
  ValidationRuleFn,
  ValidationRuleOptions,
  ValidationRuleOverrides,
} from "@/lib/validation/validation.types";

function paramsToMessageParams<ModelValue, Params>({
  label,
  modelValue,
  params,
  name,
}: {
  label: string;
  modelValue: NoInfer<ModelValue>;
  name: string;
  params: Params;
}) {
  const baseParams = { _field_: label, modelValue: String(modelValue) };
  if (!params) {
    return baseParams;
  }
  if (isObject(params)) {
    return { ...baseParams, ...mapValues(params, String) };
  }
  return { ...baseParams, [name]: String(params) };
}

function getMessageFunction<ModelValue, Params>(
  rule: ValidationRuleOptions<ModelValue, Params>,
  params: Params,
): (label: string, modelValue: NoInfer<ModelValue>) => string {
  const message = rule.message;

  return (label: string, modelValue: NoInfer<ModelValue>) => {
    if (typeof message !== "function") {
      return message;
    }
    return message({
      modelValue,
      params,
      messageParams: paramsToMessageParams({
        label,
        modelValue,
        name: rule.name,
        params,
      }),
    });
  };
}

function defineRule<
  ModelValue,
  Params,
  Return extends Promise<boolean> | boolean,
  Instance,
>(
  validate: ValidationRuleFn<ModelValue, Params, Return, Instance>,
  options:
    | ValidationRuleOptions<ModelValue, Params>
    | ((
        locale: Ref<LocaleMorpheus>,
      ) => ValidationRuleOptions<ModelValue, Params>),
  instanceInit?: () => Instance,
): {
  (
    params?: Params,
    overrides?: ValidationRuleOverrides<ModelValue, Params>,
  ): ValidationRule<ModelValue, Params, Return>;
  id: symbol;
} {
  const ruleId = Symbol();

  const ruleFunction = (
    params?: Params,
    overrides?: ValidationRuleOverrides<ModelValue, Params>,
  ): ValidationRule<ModelValue, Params, Return> => {
    const optionsInstance = (locale: Ref<LocaleMorpheus>) => {
      const optionsObject =
        typeof options === "function" ? options(locale) : options;
      const finalRule: ValidationRuleOptions<ModelValue, Params> = overrides
        ? { ...optionsObject, ...overrides }
        : optionsObject;

      return {
        ...finalRule,
        message: getMessageFunction(finalRule, params!),
        params: params!,
      };
    };

    const instance = instanceInit?.();

    return {
      id: ruleId,
      validate: (value: ModelValue) => validate(value, params!, instance!),
      options: optionsInstance,
    };
  };

  ruleFunction.id = ruleId;

  return ruleFunction;
}

export { defineRule };
